import { $dt, definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { AuraBaseDesignTokens } from '@primeng/themes/aura/base';
import { Preset } from '@primeng/themes/types';

import { generateColorPalette, generatePaletteTokens } from '@fsf/util';

// weights matching the former hand-made mblty palettes
const paletteOptions: Parameters<typeof generateColorPalette>[1] = {
  lightenWeights: [0.95, 0.76, 0.57, 0.38, 0.19],
  darkenWeights: [0.15, 0.3, 0.45, 0.6, 0.75],
};
const accent = generateColorPalette('#b1ff00', paletteOptions);
const dark = generateColorPalette('#24353d', paletteOptions);

const preset: Preset<AuraBaseDesignTokens> = {
  extend: {
    primitive: {
      accent,
      dark,
    },
    semantic: {
      secondary: generatePaletteTokens('accent'),
    },
  },
  primitive: {
    borderRadius: {
      xl: '14px',
    },
  },
  semantic: {
    primary: generatePaletteTokens('dark'),
    colorScheme: {
      light: {
        text: {
          color: '{primary.500}',
          mutedColor: '{dark.300}',
          hoverMutedColor: '{dark.400}',
        },
        content: {
          hoverBackground: '{dark.50}',
          borderColor: 'color-mix(in srgb, {dark.100}, white 45%)',
        },
        formField: {
          borderColor: '{dark.100}',
          hoverBorderColor: '{dark.200}',
        },
      },
    },
    formField: {
      borderRadius: '{border.radius.xl}',
      paddingY: '0.4rem',
      // accent glow instead of outline
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: '0 0 0 3px color-mix(in srgb, {accent.500}, transparent 67%)',
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '10px',
      },
      colorScheme: {
        light: {
          root: {
            secondary: {
              background: '{secondary.500}',
              hoverBackground: '{secondary.600}',
              activeBackground: '{secondary.700}',
              borderColor: '{secondary.500}',
              hoverBorderColor: '{secondary.600}',
              activeBorderColor: '{secondary.700}',
              color: '{primary.500}',
              hoverColor: '{primary.500}',
              activeColor: '{primary.500}',
            },
            // success = brand green, independent of secondary
            success: {
              background: '{accent.500}',
              hoverBackground: '{accent.600}',
              activeBackground: '{accent.700}',
              borderColor: '{accent.500}',
              hoverBorderColor: '{accent.600}',
              activeBorderColor: '{accent.700}',
              color: '{primary.500}',
              hoverColor: '{primary.500}',
              activeColor: '{primary.500}',
            },
          },
          text: {
            contrast: {
              color: '{secondary.500}',
              hoverBackground: '#b1ff002a', //secondary transparent
              activeBackground: '#b1ff002a', //secondary transparent
            },
          },
        },
      },
    },
    datatable: {
      headerCell: {
        padding: '0.3rem 1rem',
        color: '{dark.400}',
      },
      bodyCell: {
        padding: '0.3rem 1rem',
      },
      columnTitle: {
        fontWeight: '300',
      },
      colorScheme: {
        light: {
          // separation by row borders, no stripes
          row: {
            stripedBackground: '{content.background}',
          },
        },
      },
    },
    tabs: {
      tablist: {
        background: 'transparent',
      },
      tab: {
        padding: '0.8125rem 0.75rem 0.6875rem',
        fontWeight: '500',
      },
      activeBar: {
        height: '2px',
      },
      tabpanel: {
        background: 'transparent',
        padding: '1rem 0 0',
      },
    },
    badge: {
      dot: {
        size: '0.4375rem',
      },
      colorScheme: {
        light: {
          // success = brand green, like button
          success: {
            background: '{accent.500}',
            color: '{primary.500}',
          },
        },
      },
    },
    tag: {
      root: {
        fontSize: '0.75rem',
        fontWeight: '500',
        padding: '0.125rem 0.5rem',
        borderRadius: '10px',
      },
      colorScheme: {
        light: {
          secondary: {
            background: '{dark.50}',
            color: '{dark.400}',
          },
          success: {
            background: '{accent.100}',
            color: '{accent.900}',
          },
          warn: {
            background: '#fdf1e6',
            color: '#b35c10',
          },
          danger: {
            background: '#fdecec',
            color: '#d12c34',
          },
        },
      },
    },
    metergroup: {
      meters: {
        size: '0.375rem',
      },
    },
    progressbar: {
      root: {
        height: '0.375rem',
        borderRadius: '3px',
      },
      value: {
        background: '{secondary.500}',
      },
    },
    menubar: {
      root: {
        borderRadius: '0',
      },
      colorScheme: {
        light: {
          root: {
            background: '{primary.500}',
            borderColor: 'transparent',
          },
          item: {
            color: '#fff',
            focusBackground: 'transparent',
          },
        },
      },
    },
    panelmenu: {
      root: { gap: '0' },
      panel: {
        first: {
          borderWidth: '0',
        },
        last: {
          borderWidth: '0',
        },
        borderWidth: '0',
        padding: '0 0.25rem',
      },
      item: {
        padding: '0.25rem 0.5rem',
      },
      colorScheme: {
        light: {
          root: {},
        },
      },
      css: ({ dt }: { dt: typeof $dt }) => `
        .layout-sidebar {
            box-shadow: 0px 2px 15px -3px rgba(0, 0, 0, 0.1);
        }
        `,
    },
    card: {
      body: {
        padding: '1.125rem 1.375rem',
      },
      title: {
        fontWeight: '700',
      },
      colorScheme: {
        light: {
          root: {
            shadow: '0 2px 15px -3px rgba(0,0,0,.1)',
          },
        },
      },
    },
    chip: {
      root: {
        paddingY: '0.3rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '#f5f5f5',
          },
        },
      },
    },
    autocomplete: {
      chip: { borderRadius: '16px' },
    },
    breadcrumb: {
      root: {
        padding: '1rem 0',
        background: 'transparent',
      },
    },
  },
};

export const colors = {
  palette: [
    '#4C7D77',
    '#24353D',
    '#DDFF91',
    '#0E1518',
    '#7CB300',
    '#476600',
    '#166DFF',
    '#3A03B0',
    '#C4256A',
    '#D12C34',
    '#EA7D1F',
    '#F1AC27',
  ],
  map: {
    location: accent[500],
    markerBorder: dark[500],
  },
};

export const links = {
  logoSrc: 'assets/images/mblty-logo.svg',
  logoAlt: 'Mblty Logo',
  impressum: 'https://mblty.de/impressum',
};

export const CustomPreset = definePreset(Aura, preset);
